import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

/**
 * Búsqueda global (Ctrl+K / Cmd+K)
 * Busca en: mensajes, contactos, chats
 */
export default function GlobalSearch({ onChatSelect }) {
  const [abierto, setAbierto] = useState(false)
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState({ mensajes: [], contactos: [], chats: [] })
  const [buscando, setBuscando] = useState(false)
  const [selIdx, setSelIdx] = useState(0)
  const inputRef = useRef(null)
  const navigate = useNavigate()

  // Ctrl+K para abrir/cerrar
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setAbierto(a => !a)
        setQ('')
        setResultados({ mensajes: [], contactos: [], chats: [] })
      }
      if (e.key === 'Escape') setAbierto(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (abierto) setTimeout(() => inputRef.current?.focus(), 50)
  }, [abierto])

  // Buscar con debounce
  useEffect(() => {
    if (!q.trim() || q.length < 2) {
      setResultados({ mensajes: [], contactos: [], chats: [] })
      return
    }
    const t = setTimeout(async () => {
      setBuscando(true)
      try {
        const { data } = await api.get('/buscar', { params: { q } })
        setResultados(data)
        setSelIdx(0)
      } catch {}
      finally { setBuscando(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  // Lista plana para navegar con teclado
  const todos = [
    ...resultados.chats.map(x => ({ tipo: 'chat', ...x })),
    ...resultados.contactos.map(x => ({ tipo: 'contacto', ...x })),
    ...resultados.mensajes.map(x => ({ tipo: 'mensaje', ...x })),
  ]

  const irA = useCallback((item) => {
    setAbierto(false)
    setQ('')
    if (item.tipo === 'chat' || item.tipo === 'mensaje') {
      onChatSelect?.(item.chatId || item.id)
      navigate('/inbox')
    } else if (item.tipo === 'contacto') {
      navigate('/contactos')
    }
  }, [navigate, onChatSelect])

  useEffect(() => {
    const handler = (e) => {
      if (!abierto) return
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelIdx(i => Math.min(i + 1, todos.length - 1)) }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelIdx(i => Math.max(i - 1, 0)) }
      if (e.key === 'Enter' && todos[selIdx]) irA(todos[selIdx])
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [abierto, todos, selIdx, irA])

  if (!abierto) return null

  const highlight = (texto, q) => {
    if (!q || !texto) return texto
    const idx = texto.toLowerCase().indexOf(q.toLowerCase())
    if (idx < 0) return texto
    return (
      <>
        {texto.slice(0, idx)}
        <mark style={{ background: '#fde68a', borderRadius: 2, padding: '0 1px' }}>{texto.slice(idx, idx + q.length)}</mark>
        {texto.slice(idx + q.length)}
      </>
    )
  }

  let flatIdx = 0

  const Seccion = ({ titulo, items, renderItem }) => {
    if (!items.length) return null
    return (
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', padding: '10px 16px 4px', textTransform: 'uppercase', letterSpacing: 1 }}>
          {titulo}
        </div>
        {items.map((item, i) => {
          const idx = flatIdx++
          return (
            <div key={i} onClick={() => irA(item)}
              style={{ padding: '9px 16px', cursor: 'pointer', borderRadius: 8, margin: '0 6px',
                background: selIdx === idx ? 'var(--bg-active)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: 10 }}
              onMouseEnter={() => setSelIdx(idx)}>
              {renderItem(item)}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={() => setAbierto(false)}>
      <div style={{ width: '100%', maxWidth: 600, background: 'white', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Buscar mensajes, contactos, chats..."
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 16, background: 'transparent', color: 'var(--text)' }} />
          {buscando && <div className="spinner" style={{ width: 18, height: 18 }} />}
          <kbd style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: 'var(--text-muted)' }}>Esc</kbd>
        </div>

        {/* Resultados */}
        <div style={{ maxHeight: 440, overflowY: 'auto' }}>
          {!q && (
            <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Escribe para buscar en mensajes, contactos y chats
            </div>
          )}

          {q.length >= 2 && todos.length === 0 && !buscando && (
            <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Sin resultados para <strong>"{q}"</strong>
            </div>
          )}

          {(() => { flatIdx = 0; return null })()}

          <Seccion titulo="💬 Chats" items={resultados.chats}
            renderItem={item => (
              <>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--primary-light)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                  {(item.nombreContacto || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{highlight(item.nombreContacto, q)}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.ultimoMensaje}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>💬</span>
              </>
            )} />

          <Seccion titulo="👥 Contactos" items={resultados.contactos}
            renderItem={item => (
              <>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#6366f1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                  {(item.nombre || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{highlight(item.nombre, q)}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{highlight(item.telefono, q)}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>👤</span>
              </>
            )} />

          <Seccion titulo="📝 Mensajes" items={resultados.mensajes}
            renderItem={item => (
              <>
                <div style={{ fontSize: 18, flexShrink: 0 }}>💬</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>
                    {item.nombreContacto}
                  </div>
                  <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {highlight(item.texto, q)}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                  {new Date(item.createdAt).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                </span>
              </>
            )} />
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-muted)' }}>
          <span><kbd style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>↑↓</kbd> navegar</span>
          <span><kbd style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>Enter</kbd> abrir</span>
          <span><kbd style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>Esc</kbd> cerrar</span>
        </div>
      </div>
    </div>
  )
}
